import {
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Badge,
  Box,
  CloseIcon,
  Column,
  EmptyState,
  IconButton,
  MultiSelect,
  Row,
  Select,
  Skeleton,
  Text,
  TextInput,
  Tooltip,
} from "@hightouch/htui";

import {
  MediaDetailField,
  MediaDetailFieldSkeleton,
} from "src/components/media-detail";
import {
  type EditableTag,
  sameTagValues,
  tagChipValues,
  tagInputValue,
  tagMultiSelectOptions,
  tagSelectOptions,
  tagValueControlKind,
} from "./tag-values";

type Props = {
  tags: EditableTag[];
  isLoading?: boolean;
  isDisabled?: boolean | ((tag: EditableTag) => boolean);
  emptyMessage?: string;
  onChangeValue?: (tag: EditableTag, value: string) => Promise<void>;
  /** Saves a multi-value tag. Without it, multi-value tags render as chips. */
  onChangeValues?: (tag: EditableTag, values: string[]) => Promise<void>;
  onClearValue?: (tag: EditableTag) => Promise<void>;
};

export function EditableTagList({
  tags,
  isLoading = false,
  isDisabled = false,
  emptyMessage,
  onChangeValue = async () => undefined,
  onChangeValues,
  onClearValue,
}: Props) {
  if (isLoading) {
    return <TagListSkeleton />;
  }

  if (tags.length === 0) {
    if (!emptyMessage) return null;
    return <EmptyState variant="minimal" message={emptyMessage} />;
  }

  return (
    <Column>
      {tags.map((tag) => (
        <MediaDetailField
          key={tag.id}
          label={tag.name}
          labelTooltip={tag.labelTooltip}
        >
          <Row align="center" gap={2} flexWrap="wrap" minWidth={0}>
            <TagValueControl
              tag={tag}
              isDisabled={
                typeof isDisabled === "function" ? isDisabled(tag) : isDisabled
              }
              onChangeValue={onChangeValue}
              onChangeValues={onChangeValues}
            />
            {onClearValue && tagChipValues(tag.value).length > 0 && (
              <Tooltip message="Remove tag">
                <IconButton
                  aria-label="Remove tag"
                  icon={CloseIcon}
                  size="sm"
                  variant="tertiary"
                  isDisabled={
                    typeof isDisabled === "function"
                      ? isDisabled(tag)
                      : isDisabled
                  }
                  onClick={() => {
                    void onClearValue(tag);
                  }}
                />
              </Tooltip>
            )}
          </Row>
        </MediaDetailField>
      ))}
    </Column>
  );
}

function TagListSkeleton() {
  return (
    <Skeleton isLoading>
      <Column>
        {[0, 1, 2].map((row) => (
          <MediaDetailFieldSkeleton key={row} />
        ))}
      </Column>
    </Skeleton>
  );
}

function TagValueControl({
  tag,
  isDisabled,
  onChangeValue,
  onChangeValues,
}: {
  tag: EditableTag;
  isDisabled: boolean;
  onChangeValue: (tag: EditableTag, value: string) => Promise<void>;
  onChangeValues?: (tag: EditableTag, values: string[]) => Promise<void>;
}) {
  const save = (value: string) => onChangeValue(tag, value);
  const kind = tagValueControlKind(tag);
  const tooltip = tag.valueTooltip;

  if (kind === "multi-select" && tag.allowedValues && onChangeValues) {
    const multiSelect = (
      // Shrinks with the row so a long value list truncates instead of overflowing the field.
      <Box flex="1" minWidth={0}>
        <TagValueMultiSelect
          tag={tag}
          allowedValues={tag.allowedValues}
          isDisabled={isDisabled}
          onCommit={(values) => onChangeValues(tag, values)}
        />
      </Box>
    );
    if (!tooltip) return multiSelect;
    return <Tooltip message={tooltip}>{multiSelect}</Tooltip>;
  }

  if (kind === "select" && tag.allowedValues) {
    const select = (
      <TagValueSelect
        tag={tag}
        allowedValues={tag.allowedValues}
        isDisabled={isDisabled}
        onSelect={save}
      />
    );
    if (!tooltip) return select;
    return (
      <Tooltip message={tooltip}>
        <Box as="span" display="inline-flex">
          {select}
        </Box>
      </Tooltip>
    );
  }

  if (kind === "input") {
    const input = (
      <TagValueInput tag={tag} isDisabled={isDisabled} onCommit={save} />
    );
    if (!tooltip) return input;
    return (
      <Tooltip message={tooltip}>
        <Box as="span" display="inline-flex">
          {input}
        </Box>
      </Tooltip>
    );
  }

  return <TagChips values={tagChipValues(tag.value)} tooltip={tooltip} />;
}

function TagChips({
  values,
  tooltip,
}: {
  values: string[];
  tooltip?: string | null;
}) {
  const chips =
    values.length === 0 ? (
      <Text size="sm" color="text.secondary">
        —
      </Text>
    ) : (
      <>
        {values.map((value) => (
          <Badge key={value} variant="subtle" size="sm">
            {value}
          </Badge>
        ))}
      </>
    );
  if (!tooltip) return chips;
  return (
    <Tooltip message={tooltip}>
      <Row
        as="span"
        tabIndex={0}
        gap={2}
        flexWrap="wrap"
        borderRadius="sm"
        _focusVisible={{ boxShadow: "outline", outline: "none" }}
      >
        {chips}
      </Row>
    </Tooltip>
  );
}

function TagValueSelect({
  tag,
  allowedValues,
  isDisabled,
  onSelect,
}: {
  tag: EditableTag;
  allowedValues: string[];
  isDisabled: boolean;
  onSelect: (value: string) => Promise<void>;
}) {
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const savedValue = tagInputValue(tag.value);
  const current = pendingValue ?? savedValue;
  const options = useMemo(
    () => tagSelectOptions(allowedValues, current).map((value) => ({ value })),
    [allowedValues, current],
  );

  const savedValueRef = useRef(savedValue);
  savedValueRef.current = savedValue;
  const savingValueRef = useRef<string | null>(null);

  const pickValue = async (value: string | undefined) => {
    if (
      !value ||
      value === savedValueRef.current ||
      value === savingValueRef.current
    ) {
      return;
    }
    savingValueRef.current = value;
    setPendingValue(value);
    try {
      await onSelect(value);
    } finally {
      savingValueRef.current = null;
      setPendingValue(null);
    }
  };

  return (
    <Select
      aria-label={tag.name}
      size="sm"
      variant="alternative"
      width="auto"
      placeholder="Select a value"
      options={options}
      optionLabel={(option) => option.value}
      value={current || undefined}
      isDisabled={isDisabled}
      onChange={pickValue}
    />
  );
}

function TagValueMultiSelect({
  tag,
  allowedValues,
  isDisabled,
  onCommit,
}: {
  tag: EditableTag;
  allowedValues: string[];
  isDisabled: boolean;
  onCommit: (values: string[]) => Promise<void>;
}) {
  // Holds the picks while the menu is open, so one save covers the whole edit.
  const [draft, setDraft] = useState<string[] | null>(null);
  const savedValues = useMemo(() => tagChipValues(tag.value), [tag.value]);
  const current = draft ?? savedValues;
  const options = useMemo(
    () =>
      tagMultiSelectOptions(allowedValues, current).map((value) => ({
        value,
      })),
    [allowedValues, current],
  );

  const commit = async () => {
    if (!draft) return;
    if (sameTagValues(draft, savedValues)) {
      setDraft(null);
      return;
    }
    try {
      await onCommit(draft);
    } finally {
      setDraft(null);
    }
  };

  return (
    <MultiSelect
      aria-label={tag.name}
      size="sm"
      variant="alternative"
      width="100%"
      placeholder="Select values"
      options={options}
      optionLabel={(option) => option.value}
      value={current}
      isDisabled={isDisabled}
      onChange={setDraft}
      onClose={() => {
        void commit();
      }}
    />
  );
}

function TagValueInput({
  tag,
  isDisabled,
  onCommit,
}: {
  tag: EditableTag;
  isDisabled: boolean;
  onCommit: (value: string) => Promise<void>;
}) {
  const savedValue = tagInputValue(tag.value);
  const [draft, setDraft] = useState(savedValue);
  const savedValueRef = useRef(savedValue);
  savedValueRef.current = savedValue;
  const savingValueRef = useRef<string | null>(null);

  useEffect(() => {
    setDraft(savedValue);
  }, [savedValue]);

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === savedValueRef.current) {
      setDraft(savedValueRef.current);
      return;
    }
    if (next === savingValueRef.current) return;
    savingValueRef.current = next;
    try {
      await onCommit(next);
    } finally {
      savingValueRef.current = null;
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void commit();
    }
  };

  return (
    <TextInput
      aria-label={tag.name}
      size="sm"
      width="auto"
      type={tag.valueType === "number" ? "number" : "text"}
      value={draft}
      isDisabled={isDisabled}
      placeholder={
        tag.valueType === "number" ? "Enter a number" : "Enter a value"
      }
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        void commit();
      }}
      onKeyDown={onKeyDown}
    />
  );
}
